/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AssignRolesToUserRequest } from '../models/assign-roles-to-user-request';
import { CreateUserRequest } from '../models/create-user-request';
import { GenericResponse } from '../models/generic-response';
import { GetUsersResponse } from '../models/get-users-response';
import { UpdateUserRequest } from '../models/update-user-request';
import { UserDetailedResponse } from '../models/user-detailed-response';
import { UserProfileResponse } from '../models/user-profile-response';
import { UserResponse } from '../models/user-response';

/**
 * User management apis
 */
@Injectable({ providedIn: 'root' })
export class UsersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllUsers()` */
  static readonly GetAllUsersPath = '/api/v1/users';

  /**
   * Get list of users.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUsers$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name - username - email - designation - epn
       */
      search?: string;

      /**
       * This adds a search to the id field. - ids
       */
      ids?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetUsersResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.GetAllUsersPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('ids', params.ids, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetUsersResponse>;
        })
      );
  }

  /**
   * Get list of users.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUsers(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name - username - email - designation - epn
       */
      search?: string;

      /**
       * This adds a search to the id field. - ids
       */
      ids?: Array<number>;
    },
    context?: HttpContext
  ): Observable<GetUsersResponse> {
    return this.getAllUsers$Response(params, context).pipe(
      map((r: StrictHttpResponse<GetUsersResponse>): GetUsersResponse => r.body)
    );
  }

  /** Path part for operation `createUser()` */
  static readonly CreateUserPath = '/api/v1/users';

  /**
   * Create a new user.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createUser$Response(
    params: {
      body: CreateUserRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.CreateUserPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserResponse>;
        })
      );
  }

  /**
   * Create a new user.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createUser(
    params: {
      body: CreateUserRequest;
    },
    context?: HttpContext
  ): Observable<UserResponse> {
    return this.createUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<UserResponse>): UserResponse => r.body)
    );
  }

  /** Path part for operation `getLoggedInUserDetails()` */
  static readonly GetLoggedInUserDetailsPath = '/api/v1/users/me';

  /**
   * Get logged in user details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getLoggedInUserDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  getLoggedInUserDetails$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserProfileResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.GetLoggedInUserDetailsPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserProfileResponse>;
        })
      );
  }

  /**
   * Get logged in user details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getLoggedInUserDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getLoggedInUserDetails(
    params?: {},
    context?: HttpContext
  ): Observable<UserProfileResponse> {
    return this.getLoggedInUserDetails$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserProfileResponse>): UserProfileResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getUserById()` */
  static readonly GetUserByIdPath = '/api/v1/users/{id}';

  /**
   * Get user details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUserById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserById$Response(
    params: {
      /**
       * The user's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.GetUserByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserDetailedResponse>;
        })
      );
  }

  /**
   * Get user details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUserById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserById(
    params: {
      /**
       * The user's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<UserDetailedResponse> {
    return this.getUserById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserDetailedResponse>): UserDetailedResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `softDeleteUser()` */
  static readonly SoftDeleteUserPath = '/api/v1/users/{id}';

  /**
   * Soft delete users.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `softDeleteUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteUser$Response(
    params: {
      /**
       * The user's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.SoftDeleteUserPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Soft delete users.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `softDeleteUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteUser(
    params: {
      /**
       * The user's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.softDeleteUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateUser()` */
  static readonly UpdateUserPath = '/api/v1/users/{id}';

  /**
   * Update user by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateUser$Response(
    params: {
      /**
       * The user's id that needs to be updated
       */
      id: number;
      body: UpdateUserRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.UpdateUserPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserResponse>;
        })
      );
  }

  /**
   * Update user by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateUser(
    params: {
      /**
       * The user's id that needs to be updated
       */
      id: number;
      body: UpdateUserRequest;
    },
    context?: HttpContext
  ): Observable<UserResponse> {
    return this.updateUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<UserResponse>): UserResponse => r.body)
    );
  }

  /** Path part for operation `getInternalUserById()` */
  static readonly GetInternalUserByIdPath = '/api/v1/internal-users/{id}';

  /**
   * Get user details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getInternalUserById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getInternalUserById$Response(
    params: {
      /**
       * The user's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.GetInternalUserByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserDetailedResponse>;
        })
      );
  }

  /**
   * Get user details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getInternalUserById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getInternalUserById(
    params: {
      /**
       * The user's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<UserDetailedResponse> {
    return this.getInternalUserById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserDetailedResponse>): UserDetailedResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `assignRolesToUser()` */
  static readonly AssignRolesToUserPath =
    '/api/v1/users/{user-id}/assign-roles';

  /**
   * Assign roles to user.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `assignRolesToUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  assignRolesToUser$Response(
    params: {
      /**
       * The user's id to whom the roles to be assigned
       */
      'user-id': number;
      body: AssignRolesToUserRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UsersService.AssignRolesToUserPath,
      'post'
    );
    if (params) {
      rb.path('user-id', params['user-id'], {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Assign roles to user.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `assignRolesToUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  assignRolesToUser(
    params: {
      /**
       * The user's id to whom the roles to be assigned
       */
      'user-id': number;
      body: AssignRolesToUserRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.assignRolesToUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }
}
